import React, {useEffect, useMemo, useRef, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {Panel} from "primereact/panel";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import {FileUpload} from "primereact/fileupload";
import {Message} from "primereact/message";
import {Calendar} from "primereact/calendar";
import {InputMask} from "primereact/inputmask";
import {addLocale} from "primereact/api";
import axios from "axios";
import Cookies from "js-cookie";
import moment from "moment/moment";

import LanglinksComponent from "../components/LanglinksComponent";

const _FM_FastchekinPage = (props) => {
  const {t} = props;

  const [reservation, setReservation] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [requiredFields, setRequiredFields] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  const [stage, setStage] = useState('scan'); // scan | edit
  const [loading, setLoading] = useState(false);
  const [documentUploadLoading, setDocumentUploadLoading] = useState(false);
  const [documentScanType, setDocumentScanType] = useState('id');
  const [documentScanCountry, setDocumentScanCountry] = useState('hu');

  const documentUploadRef = useRef(null);

  const customerCustomFields = useMemo(
    () => (customFields || []).filter((field) => field?.related_to === 'RELATED_TO_CUSTOMER'),
    [customFields]
  );
  const normalizeFieldKey = (value) => (value || '').toString().trim().replace(/[_\s-]/g, '').toLowerCase();
  const normalizedRequiredFields = new Set((requiredFields || []).map((field) => normalizeFieldKey(field)).filter(Boolean));
  const savedGuestsCount = useMemo(
    () => (customers || []).length,
    [customers]
  );

  const getEmptyFormData = () => {
    const dynamicFields = customerCustomFields.reduce((acc, field) => {
      if (field?.name) {
        acc[field.name] = '';
      }
      return acc;
    }, {});

    return {
      firstname: '',
      lastname: '',
      gender: '',
      citizenship: '',
      residence_country: '',
      birth_date: '',
      birth_place: '',
      id_type: 'PASSPORT',
      id_number: '',
      zip_code: '',
      address: '',
      company_name: '',
      email: '',
      phone: '',
      note: '',
      expected_check_in_at: '',
      ...dynamicFields
    };
  };

  const [formData, setFormData] = useState(getEmptyFormData());

  addLocale('default', {
    firstDayOfWeek: 1,
    dayNames: [t('sunday'), t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday'), t('saturday')],
    dayNamesShort: [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')],
    dayNamesMin: [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')],
    monthNames: [t('jan'), t('feb'), t('mar'), t('apr'), t('may'), t('jun'), t('jul'), t('aug'), t('sep'),
      t('oct'), t('nov'), t('dec')],
    monthNamesShort: [t('jan'), t('feb'), t('mar'), t('apr'), t('may'), t('jun'), t('jul'), t('aug'), t('sep'),
      t('oct'), t('nov'), t('dec')],
    today: t('today'),
    clear: t('delete')
  });

  const getSysName = () => window.location.href.split("//")[1].split(".")[0].replaceAll('-', '_');
  const getUniqueId = () => window.App.urlVars().uniqueId;
  const getPassword = () => window.App.urlVars().password;

  const getPublicHeaders = () => ({
    "Sys-name": getSysName(),
    "Preferred-locale": Cookies.get("language")
  });

  const remainingGuestSlots = Math.max((reservation?.number_of_guests || 0) - (customers?.length || 0), 0);

  const resetToScan = () => {
    setFormData(getEmptyFormData());
    setStage('scan');
  };

  const loadReservation = () => {
    if (!getUniqueId() || !getPassword()) {
      return;
    }

    setLoading(true);
    axios.get(
      process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "public/reservation/"
      + getUniqueId() + "/" + getPassword(),
      {
        headers: getPublicHeaders()
      }
    )
      .then((response) => {
        const data = response?.data?.data || {};
        setReservation(data);
        setCustomers(data?.customers || []);
        setRequiredFields(data?.guest_information_request_page?.customer_required_fields || []);
        setCustomFields(data?.guest_information_request_page?.girp_additional_fields || []);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadCountries = () => {
    axios({
      method: 'get',
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "dictionary/country",
      headers: getPublicHeaders()
    })
      .then((response) => {
        const allCountries = response?.data?.data || [];
        const prioritizedCountries = JSON.parse(process.env.REACT_APP_PRIORITIZEDCOUNTRIES || "[]");
        const sortedCountries = [
          ...allCountries.filter((country) => prioritizedCountries.includes(country.code)),
          ...allCountries.filter((country) => !prioritizedCountries.includes(country.code)),
        ];
        setCountries(sortedCountries);
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
      });
  };

  useEffect(() => {
    document.title = t('checkin_form') + ' - ' + process.env.REACT_APP_TITLE;
    const root = document.getElementById('root');
    const previousHtmlBackground = document.documentElement.style.backgroundColor;
    if (root) {
      root.classList.add('public');
    }
    document.documentElement.style.backgroundColor = '#ffffff';

    loadReservation();
    loadCountries();

    return () => {
      if (root) {
        root.classList.remove('public');
      }
      document.documentElement.style.backgroundColor = previousHtmlBackground;
    };
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...getEmptyFormData(),
      ...prev
    }));
  }, [customFields]);

  const getFirstNonEmptyValue = (data, keys = []) => {
    for (const key of keys) {
      const value = data?.[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return undefined;
  };

  const splitFullName = (fullName = '', options = {}) => {
    const {familyNameFirst = false} = options;
    const normalized = (fullName || '').trim().replace(/\s+/g, ' ');
    if (!normalized) {
      return {firstname: '', lastname: ''};
    }

    const parts = normalized.split(' ');
    if (parts.length === 1) {
      return {firstname: parts[0], lastname: ''};
    }

    if (familyNameFirst) {
      return {
        firstname: parts.slice(1).join(' '),
        lastname: parts[0]
      };
    }

    return {
      firstname: parts[0],
      lastname: parts.slice(1).join(' ')
    };
  };

  const normalizeCountryCode = (value) => {
    if (value === undefined || value === null) {
      return undefined;
    }

    const rawValue = value.toString().trim();
    if (!rawValue) {
      return undefined;
    }

    const normalizedUpper = rawValue.toUpperCase();
    const normalizedLower = rawValue.toLowerCase();

    const byCode = countries.find((country) => (country?.code || '').toString().toUpperCase() === normalizedUpper);
    if (byCode?.code) {
      return byCode.code;
    }

    const byAlpha3 = countries.find((country) => {
      const alpha3 = country?.alpha3 || country?.alpha3_code || country?.iso3 || '';
      return alpha3.toString().toUpperCase() === normalizedUpper;
    });
    if (byAlpha3?.code) {
      return byAlpha3.code;
    }

    const byName = countries.find((country) => (country?.name || '').toString().toLowerCase() === normalizedLower);
    if (byName?.code) {
      return byName.code;
    }

    if (rawValue.length <= 3) {
      return normalizedUpper;
    }

    return rawValue;
  };

  const getValidatedBirthDate = (value) => {
    if (!value) {
      return '';
    }

    const parsedBirthDate = moment(value, 'YYYY-MM-DD', true);
    if (!parsedBirthDate.isValid()) {
      return '';
    }

    const minAllowedBirthYear = moment().year() - 99;
    if (parsedBirthDate.year() < minAllowedBirthYear) {
      window.App.customToastShow('warn', t('warning'), t('birth_date_too_old_warning'));
      return '';
    }

    return parsedBirthDate.format('YYYY-MM-DD');
  };

  const applyScannedCustomerData = (scanData = {}) => {
    const fields = scanData?.fields || {};
    const issuingCountryRaw = getFirstNonEmptyValue(fields, ['issuingCountry', 'issuing_country'])
      ?? getFirstNonEmptyValue(scanData, ['issuingCountry', 'issuing_country']);
    const scanCountryRaw = getFirstNonEmptyValue(scanData, ['country']) || issuingCountryRaw;
    const scanCountry = normalizeCountryCode(scanCountryRaw);
    const citizenshipCountry = normalizeCountryCode(issuingCountryRaw);
    const familyNameFirstCountries = ['cn', 'hu', 'jp', 'kp', 'kr', 'mn', 'tw', 'vn'];
    const scanIndicatesFamilyNameFirst = familyNameFirstCountries.includes((scanCountry || '').toString().toLowerCase());
    const scannedFirstnameDirect = getFirstNonEmptyValue(fields, ['firstName', 'firstname', 'givenName', 'givenNames', 'forename', 'forenames', 'name']);
    const scannedLastnameDirect = getFirstNonEmptyValue(fields, ['lastName', 'lastname', 'surname', 'familyName', 'familyNames']);
    const {firstname: scannedFirstnameFromFullName, lastname: scannedLastnameFromFullName} = splitFullName(
      fields?.fullName || '',
      {familyNameFirst: scanIndicatesFamilyNameFirst}
    );
    const scannedFirstname = scannedFirstnameDirect || scannedFirstnameFromFullName;
    const scannedLastname = scannedLastnameDirect || scannedLastnameFromFullName;
    const normalizedBirthDateRaw = getFirstNonEmptyValue(fields, ['dateOfBirth']);
    const normalizedBirthDate = normalizedBirthDateRaw ? getValidatedBirthDate(normalizedBirthDateRaw) : '';

    const normalizedGender = (getFirstNonEmptyValue(fields, ['gender']) || '').toString().toUpperCase();
    const genderMap = {
      M: 'MALE',
      MALE: 'MALE',
      F: 'FEMALE',
      FEMALE: 'FEMALE',
      O: 'OTHER',
      X: 'OTHER',
      OTHER: 'OTHER',
    };

    const scannedIdType = (getFirstNonEmptyValue(scanData, ['scanType']) || '')
      .toString()
      .toUpperCase();
    const idTypeMap = {
      ID: 'ID_CARD',
      DRIVE_LICENSE: 'DRIVER_LICENSE',
      DRIVER_LICENSE: 'DRIVER_LICENSE',
    };

    setFormData((prev) => {
      const nextData = {
        ...prev,
        firstname: scannedFirstname || prev.firstname,
        lastname: scannedLastname || prev.lastname,
        id_number: getFirstNonEmptyValue(fields, ['documentNumber']) ?? prev.id_number,
        address: getFirstNonEmptyValue(fields, ['address']) ?? prev.address,
        citizenship: scanCountry || citizenshipCountry || prev.citizenship,
        residence_country: scanCountry || prev.residence_country,
      };

      if (normalizedBirthDate) {
        nextData.birth_date = normalizedBirthDate;
      }
      if (normalizedGender) {
        nextData.gender = genderMap[normalizedGender] || 'OTHER';
      }
      if (idTypeMap[scannedIdType]) {
        nextData.id_type = idTypeMap[scannedIdType];
      }

      return nextData;
    });
  };

  const onDocumentScanUpload = (e) => {
    const file = e?.files?.[0];
    if (!file) {
      return;
    }

    if (remainingGuestSlots <= 0) {
      window.App.toastShow('warn', t('warning'), t('maximun_guests_reached'));
      if (documentUploadRef.current) {
        documentUploadRef.current.clear();
      }
      return;
    }

    setDocumentUploadLoading(true);

    const upload = async () => {
      let uploadFile = file;

      if ((file.type || '').toLowerCase().startsWith('image/')) {
        try {
          const image = await new Promise((resolve, reject) => {
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
              URL.revokeObjectURL(objectUrl);
              resolve(img);
            };
            img.onerror = () => {
              URL.revokeObjectURL(objectUrl);
              reject(new Error('Failed to decode image file'));
            };
            img.src = objectUrl;
          });

          const maxDimension = 2200;
          const widthRatio = maxDimension / (image.width || maxDimension);
          const heightRatio = maxDimension / (image.height || maxDimension);
          const ratio = Math.min(1, widthRatio, heightRatio);
          const targetWidth = Math.max(1, Math.round((image.width || 1) * ratio));
          const targetHeight = Math.max(1, Math.round((image.height || 1) * ratio));

          const canvas = document.createElement('canvas');
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const context = canvas.getContext('2d');
          if (!context) {
            throw new Error('Canvas context is unavailable');
          }
          context.drawImage(image, 0, 0, targetWidth, targetHeight);

          let quality = 0.9;
          let jpegBlob = null;
          const maxProcessedSize = 5 * 1024 * 1024;
          while (quality >= 0.55) {
            // eslint-disable-next-line no-await-in-loop
            const attemptBlob = await new Promise((resolve, reject) => {
              canvas.toBlob((blob) => {
                if (!blob) {
                  reject(new Error('Failed to encode image'));
                  return;
                }
                resolve(blob);
              }, 'image/jpeg', quality);
            });

            jpegBlob = attemptBlob;
            if (attemptBlob.size <= maxProcessedSize) {
              break;
            }
            quality -= 0.1;
          }

          if (!jpegBlob) {
            throw new Error('Failed to process image');
          }

          const safeName = (file.name || 'document')
            .replace(/\.[^/.]+$/, '')
            .replace(/[^\w.-]/g, '_');
          uploadFile = new File([jpegBlob], safeName + '.jpg', {type: 'image/jpeg'});

          if (uploadFile.size > 8 * 1024 * 1024) {
            window.App.toastShow('warn', t('warning'), 'A kiválasztott kép túl nagy. Kérlek kisebb felbontású képet tölts fel.');
            return;
          }
        } catch (conversionError) {
          window.App.toastShow('warn', t('warning'), 'A kép feldolgozása sikertelen. Kérlek JPG vagy PNG formátumot tölts fel.');
          return;
        }
      }

      const payload = new FormData();
      const uploadFileName = (uploadFile?.name || 'document.jpg').toString().trim() || 'document.jpg';
      payload.append('file', uploadFile, uploadFileName);
      payload.append('type', documentScanType || 'id');
      payload.append('country', ((documentScanCountry || 'hu') + '').toLowerCase());

      return axios.post(
        process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST
        + "public/reservation/" + getUniqueId() + "/" + getPassword() + "/document-scan",
        payload,
        {headers: getPublicHeaders()}
      )
        .then((response) => {
          const scannedData = response?.data?.data || response?.data || {};
          applyScannedCustomerData(scannedData);
          setStage('edit');
          window.App.toastShow('success', t('success'), response?.data?.message || t('saved'));
        })
        .catch((error) => {
          window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
        });
    };

    upload()
      .finally(() => {
        setDocumentUploadLoading(false);
        if (documentUploadRef.current) {
          documentUploadRef.current.clear();
        }
      });
  };

  const validateForm = () => {
    const errors = {};
    const customFieldNames = new Set(customerCustomFields.map((field) => field?.name).filter(Boolean));
    const isBasicFieldRequired = (fieldName) => normalizedRequiredFields.has(normalizeFieldKey(fieldName));

    Object.keys(formData || {}).forEach((fieldName) => {
      if (customFieldNames.has(fieldName)) {
        return;
      }
      if (isBasicFieldRequired(fieldName) && !formData[fieldName]) {
        errors[fieldName] = 'Kötelező mező';
      }
    });

    customerCustomFields.forEach((field) => {
      if (field?.required && !formData?.[field.name]) {
        errors[field.name] = 'Kötelező mező';
      }
    });

    return errors;
  };

  const [errors, setErrors] = useState({});

  const saveGuest = () => {
    if (remainingGuestSlots <= 0) {
      window.App.toastShow('warn', t('warning'), t('maximun_guests_reached'));
      return;
    }

    const nextErrors = validateForm();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      window.App.toastShow('warn', t('warning'), 'Kérlek töltsd ki a kötelező mezőket.');
      return;
    }

    setLoading(true);
    axios.post(
      process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "public/reservation/customer/"
      + getUniqueId() + "/" + getPassword(),
      formData,
      {headers: getPublicHeaders()}
    )
      .then((response) => {
        window.App.toastShow('success', t('success'), response?.data?.message || t('saved'));
        loadReservation();
        resetToScan();
      })
      .catch((error) => {
        window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderFieldError = (fieldName) => {
    if (!errors[fieldName]) {
      return null;
    }
    return <small className="p-error">{errors[fieldName]}</small>;
  };
  const shouldRenderField = (fieldName) => normalizedRequiredFields.has(normalizeFieldKey(fieldName));

  const showMissingCredentials = !getUniqueId() || !getPassword();

  return (
    <div className="_FM_PublicchekinPage p-component p-m-lg-4">
      <style>
        {"html { background-color: #ffffff !important; }"}
      </style>
      <Panel style={{maxWidth: "960px"}} className={"p-mx-auto"}>
        <div className={"p-grid"}>
          <div className={"p-col-12 p-col-align-center p-text-center"}>
            {Cookies.get('theme') === 'dark' ? (
              <img src={require(".." + process.env.REACT_APP_LOGO_FILE_DARK)}
                   style={{maxHeight: "58px", maxWidth: "200px"}}/>
            ) : (
              <img src={require(".." + process.env.REACT_APP_LOGO_FILE)}
                   style={{maxHeight: "58px", maxWidth: "200px"}}/>
            )}
          </div>
          <div className={"p-col-12 p-lg-3 p-col-align-center p-text-center"}>
            <h2 className={"p-my-0"}>{t('fast_checkin')}</h2>
          </div>
          <div className={"p-col-12 p-lg-3 p-col-align-center p-text-right"}>
            <LanglinksComponent/>
          </div>
        </div>

        <hr className={"p-mb-2"}/>
        {!!reservation?.public_id && (
          <>
            <div className={"p-grid p-mt-2"}>
              <div className={"p-col-6"}>
                <small className={"p-text-uppercase"}>
                  {t('reservation_number')}
                </small>
                <p className={"p-mt-0 p-mb-0 p-text-bold"}>
                  {reservation.public_id}
                </p>
              </div>
              <div className={"p-col-6"}>
                <small className={"p-text-uppercase"}>
                  {t('residents_count')}
                </small>
                <p className={"p-mt-0 p-mb-0 p-text-bold"}>
                  {savedGuestsCount} / {reservation?.number_of_guests || customers.length || 0}
                </p>
              </div>
              <div className={"p-col-6"}>
                <small className={"p-text-uppercase"}>
                  {t('reserved_from')}
                </small>
                <p className={"p-mt-0 p-mb-0 p-text-bold"}>
                  {reservation?.reserved_from ? moment.utc(reservation.reserved_from).local().format(t('YYYY-MM-DD')) : '-'}
                </p>
              </div>
              <div className={"p-col-6"}>
                <small className={"p-text-uppercase"}>
                  {t('reserved_to')}
                </small>
                <p className={"p-mt-0 p-mb-0 p-text-bold"}>
                  {reservation?.reserved_to ? moment.utc(reservation.reserved_to).local().format(t('YYYY-MM-DD')) : '-'}
                </p>
              </div>
            </div>
            <hr className={"p-my-2"}/>
          </>
        )}

        {showMissingCredentials &&
          <Message severity="error" text={'Missing uniqueId or password in URL.'} className={"p-d-block"}/>
        }

        {!showMissingCredentials && reservation?.errors?.[0] &&
          <Message severity="warn" text={t('reservation_errror_checkin_disabled')} className={"p-d-block"}/>
        }

        {!showMissingCredentials && !reservation?.errors?.[0] && (
          <>
            {remainingGuestSlots <= 0 &&
              <Message severity="warn" text={t('maximun_guests_reached')} className={"p-d-block p-mb-3"}/>
            }

            {stage === 'scan' && (
              <div className={"p-grid highlighted p-mt-2"}>
                <div className={"p-col-12 p-lg-4 p-col-align-center"}>
                  <div className="p-field p-fluid p-mb-0">
                    <Dropdown options={countries}
                              filter
                              optionLabel="name"
                              optionValue="code"
                              id={"document_scan_country"}
                              emptyMessage={t('empty_message')}
                              onChange={(e) => setDocumentScanCountry(e.value)}
                              value={documentScanCountry}
                    />
                  </div>
                </div>
                <div className={"p-col-12 p-lg-4 p-col-align-center"}>
                  <div className="p-field p-fluid p-mb-0">
                    <Dropdown options={[
                      {name: t('id_card'), value: 'id'},
                      {name: t('passport'), value: 'passport'},
                      {name: t('driver_license'), value: 'drive_license'}
                    ]}
                              optionLabel="name"
                              optionValue="value"
                              id={"document_scan_type"}
                              emptyMessage={t('empty_message')}
                              onChange={(e) => setDocumentScanType(e.value)}
                              value={documentScanType}
                    />
                  </div>
                </div>
                <div className={"p-col-12 p-lg-4 p-col-align-center"}>
                  <div className="p-field p-fluid p-mb-0">
                    <FileUpload
                      ref={documentUploadRef}
                      mode="basic"
                      auto
                      chooseLabel={t('upload_id')}
                      chooseOptions={{icon: documentUploadLoading ? 'pi pi-spin pi-spinner' : 'pi pi-camera', className: 'w-100'}}
                      name="file"
                      accept="image/*,.heic,.heif"
                      disabled={documentUploadLoading || loading || remainingGuestSlots <= 0}
                      customUpload
                      uploadHandler={onDocumentScanUpload}
                    />
                  </div>
                </div>
                <div className={"p-col-12"}>
                  <Message severity="info" className={"p-d-block p-m-0 p-border-secondary p-text-center"} text={t('id_photo_not_stored')}/>
                </div>
              </div>
            )}

            {stage === 'edit' && (
              <div className={"customer new p-px-4 p-py-2 p-mt-3"}>
                <div className={"p-grid"}>
                  <div className={"p-col-12 p-col-align-center"}>
                    <h3>{t('new_guest')}</h3>
                  </div>
                  <div className={"p-col-6 p-col-align-center"}>
                    <Button type="button"
                            label={'Új beolvasás'}
                            icon={"pi pi-refresh"}
                            className={"p-button-outlined p-button-secondary  p-d-block w-100"}
                            onClick={resetToScan}
                    />
                  </div>
                  <div className={"p-col-6 p-col-align-center"}>
                  <Button type="button"
                            label={t('save')}
                            icon={"pi pi-save"}
                            loading={loading}
                            onClick={saveGuest}
                            disabled={loading || remainingGuestSlots <= 0}
                            className={"p-button-success p-d-block w-100"}
                    />
                  </div>
                </div>

                <div className={"p-grid"}>
                  {shouldRenderField('lastname') && (
                  <div className={"p-col-12 p-lg-4"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="lastname">{t('lastname')}</label>
                      <span className="p-d-block">
                        <InputText id="lastname"
                                   value={formData.lastname}
                                   className={errors.lastname ? 'p-invalid' : ''}
                                   onChange={(e) => setFormData({...formData, lastname: e.target.value})}
                        />
                      </span>
                      {renderFieldError('lastname')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('firstname') && (
                  <div className={"p-col-12 p-lg-4"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="firstname">{t('firstname')}</label>
                      <span className="p-d-block">
                        <InputText id="firstname"
                                   value={formData.firstname}
                                   className={errors.firstname ? 'p-invalid' : ''}
                                   onChange={(e) => setFormData({...formData, firstname: e.target.value})}
                        />
                      </span>
                      {renderFieldError('firstname')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('birth_date') && (
                  <div className={"p-col-12 p-lg-4"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="birth_date">{t('birth_date')}</label>
                      <span className="p-d-block">
                        <div className="p-inputgroup calendar-icon-only">
                          <InputMask id={"birth_date"}
                                     mask={'9999-99-99'}
                                     value={formData.birth_date}
                                     className={errors.birth_date ? 'p-invalid' : ''}
                                     onChange={(e) => {
                                       if ((e.value || '').indexOf("_") !== -1) {
                                         return;
                                       }
                                       const validated = getValidatedBirthDate(e.value);
                                       setFormData({...formData, birth_date: validated});
                                     }}
                          />
                          <Calendar value={formData.birth_date ? moment(formData.birth_date, 'YYYY-MM-DD').toDate() : null}
                                    locale="default"
                                    dateFormat={'yy-mm-dd'}
                                    showIcon
                                    onChange={(e) => {
                                      const value = e.value ? moment(e.value).format('YYYY-MM-DD') : '';
                                      setFormData({...formData, birth_date: getValidatedBirthDate(value)});
                                    }}/>
                        </div>
                      </span>
                      {renderFieldError('birth_date')}
                    </div>
                  </div>
                  )}

                  {shouldRenderField('birth_place') && (
                  <div className={"p-col-12 p-lg-3"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="birth_place">{t('birth_place')}</label>
                      <span className="p-d-block">
                        <InputText id="birth_place"
                                   value={formData.birth_place}
                                   className={errors.birth_place ? 'p-invalid' : ''}
                                   onChange={(e) => setFormData({...formData, birth_place: e.target.value})}
                        />
                      </span>
                      {renderFieldError('birth_place')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('gender') && (
                  <div className={"p-col-12 p-lg-3"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="gender">{t('gender')}</label>
                      <span className="p-d-block">
                        <Dropdown id="gender"
                                  options={[
                                    {name: t('male'), value: 'MALE'},
                                    {name: t('female'), value: 'FEMALE'},
                                    {name: t('other'), value: 'OTHER'},
                                  ]}
                                  optionLabel="name"
                                  optionValue="value"
                                  className={errors.gender ? 'p-invalid' : ''}
                                  value={formData.gender}
                                  onChange={(e) => setFormData({...formData, gender: e.value})}
                        />
                      </span>
                      {renderFieldError('gender')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('citizenship') && (
                  <div className={"p-col-12 p-lg-3"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="citizenship">{t('citizenship')}</label>
                      <span className="p-d-block">
                        <Dropdown id="citizenship"
                                  options={countries}
                                  filter
                                  optionLabel="name"
                                  optionValue="code"
                                  emptyMessage={t('empty_message')}
                                  className={errors.citizenship ? 'p-invalid' : ''}
                                  value={formData.citizenship}
                                  onChange={(e) => setFormData({...formData, citizenship: e.value})}
                        />
                      </span>
                      {renderFieldError('citizenship')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('residence_country') && (
                  <div className={"p-col-12 p-lg-3"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="residence_country">{t('residence_country')}</label>
                      <span className="p-d-block">
                        <Dropdown id="residence_country"
                                  options={countries}
                                  filter
                                  optionLabel="name"
                                  optionValue="code"
                                  emptyMessage={t('empty_message')}
                                  className={errors.residence_country ? 'p-invalid' : ''}
                                  value={formData.residence_country}
                                  onChange={(e) => setFormData({...formData, residence_country: e.value})}
                        />
                      </span>
                      {renderFieldError('residence_country')}
                    </div>
                  </div>
                  )}

                  {shouldRenderField('id_type') && (
                  <div className={"p-col-12 p-lg-4"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="id_type">{t('id_type')}</label>
                      <span className="p-d-block">
                        <Dropdown id="id_type"
                                  options={[
                                    {name: t('id_card'), value: 'ID_CARD'},
                                    {name: t('passport'), value: 'PASSPORT'},
                                    {name: t('driver_license'), value: 'DRIVER_LICENSE'},
                                    {name: t('other'), value: 'OTHER'},
                                  ]}
                                  optionLabel="name"
                                  optionValue="value"
                                  className={errors.id_type ? 'p-invalid' : ''}
                                  value={formData.id_type}
                                  onChange={(e) => setFormData({...formData, id_type: e.value})}
                        />
                      </span>
                      {renderFieldError('id_type')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('id_number') && (
                  <div className={"p-col-12 p-lg-4"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="id_number">{t('id_number')}</label>
                      <span className="p-d-block">
                        <InputText id="id_number"
                                   value={formData.id_number}
                                   className={errors.id_number ? 'p-invalid' : ''}
                                   onChange={(e) => setFormData({...formData, id_number: e.target.value})}
                        />
                      </span>
                      {renderFieldError('id_number')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('expected_check_in_at') && (
                  <div className={"p-col-12 p-lg-4"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="expected_check_in_at">{t('arrival_date')}</label>
                      <span className="p-d-block">
                        <div className="p-inputgroup calendar-icon-only">
                          <InputMask id={"expected_check_in_at"}
                                     mask={'9999-99-99'}
                                     value={formData.expected_check_in_at}
                                     onChange={(e) => {
                                       if ((e.value || '').indexOf("_") !== -1) {
                                         return;
                                       }
                                       setFormData({...formData, expected_check_in_at: e.value || ''});
                                     }}
                          />
                          <Calendar value={formData.expected_check_in_at ? moment(formData.expected_check_in_at, 'YYYY-MM-DD').toDate() : null}
                                    locale="default"
                                    dateFormat={'yy-mm-dd'}
                                    showIcon
                                    onChange={(e) => {
                                      const value = e.value ? moment(e.value).format('YYYY-MM-DD') : '';
                                      setFormData({...formData, expected_check_in_at: value});
                                    }}/>
                        </div>
                      </span>
                    </div>
                  </div>
                  )}

                  {shouldRenderField('zip_code') && (
                  <div className={"p-col-12 p-lg-3"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="zip_code">{t('zip_code')}</label>
                      <span className="p-d-block">
                        <InputText id="zip_code"
                                   value={formData.zip_code}
                                   className={errors.zip_code ? 'p-invalid' : ''}
                                   onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                        />
                      </span>
                      {renderFieldError('zip_code')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('address') && (
                  <div className={"p-col-12 p-lg-9"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="address">{t('address')}</label>
                      <span className="p-d-block">
                        <InputText id="address"
                                   value={formData.address}
                                   className={errors.address ? 'p-invalid' : ''}
                                   onChange={(e) => setFormData({...formData, address: e.target.value})}
                        />
                      </span>
                      {renderFieldError('address')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('company_name') && (
                  <div className={"p-col-12 p-lg-4"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="company_name">{t('company_name')}</label>
                      <span className="p-d-block">
                        <InputText id="company_name"
                                   value={formData.company_name}
                                   className={errors.company_name ? 'p-invalid' : ''}
                                   onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                        />
                      </span>
                      {renderFieldError('company_name')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('email') && (
                  <div className={"p-col-12 p-lg-4"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="email">{t('email')}</label>
                      <span className="p-d-block">
                        <InputText id="email"
                                   value={formData.email}
                                   className={errors.email ? 'p-invalid' : ''}
                                   onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                      </span>
                      {renderFieldError('email')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('phone') && (
                  <div className={"p-col-12 p-lg-4"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="phone">{t('phone')}</label>
                      <span className="p-d-block">
                        <InputText id="phone"
                                   value={formData.phone}
                                   className={errors.phone ? 'p-invalid' : ''}
                                   onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                      </span>
                      {renderFieldError('phone')}
                    </div>
                  </div>
                  )}
                  {shouldRenderField('note') && (
                  <div className={"p-col-12"}>
                    <div className="p-field p-fluid">
                      <label htmlFor="note">{t('note')}</label>
                      <span className="p-d-block">
                        <InputText id="note"
                                   value={formData.note}
                                   className={errors.note ? 'p-invalid' : ''}
                                   onChange={(e) => setFormData({...formData, note: e.target.value})}
                        />
                      </span>
                      {renderFieldError('note')}
                    </div>
                  </div>
                  )}

                  {customerCustomFields.filter((field) => field?.required).map((field) => (
                    <div className={"p-col-12 p-lg-6"} key={field.name}>
                      <div className="p-field p-fluid">
                        <label htmlFor={field.name}>{t(field.label || field.name)}</label>
                        <span className="p-d-block">
                          <InputText id={field.name}
                                     value={formData[field.name] || ''}
                                     className={errors[field.name] ? 'p-invalid' : ''}
                                     onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                          />
                        </span>
                        {renderFieldError(field.name)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Panel>
    </div>
  );
};

export default withTranslation('common')(_FM_FastchekinPage);
